import {
  Checkbox,
  FormControl,
  FormHelperText,
  IconButton,
  InputAdornment,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
} from "@mui/material";
import ClearIcon from "@mui/icons-material/Clear";
import { useState } from "react";
import { formInputsStyles } from "../../Configuration/styles";

const formatOption = (option: string | number): string => {
  const str = option.toString();
  if (str.startsWith("<")) return str;
  const cleaned = str.replace(/_/g, " ");
  return cleaned.replace(/\b\w/g, (char) => char.toUpperCase());
};

const formatOptions = (
  options: string[] | number[]
): Record<string, string> => {
  const formattedOptions: Record<string, string> = {};
  options.forEach((option) => {
    formattedOptions[option.toString()] = formatOption(option);
  });
  return formattedOptions;
};

export const CustomSelect = ({
  identifier,
  label,
  defaultValue,
  required = true,
  description = undefined,
  options,
  onChange,
  multiple = false,
  isNested = false,
  clearValue,
}: {
  identifier: string;
  label?: string;
  defaultValue: any;
  required?: boolean;
  description?: string;
  options: string[];
  onChange: (key: string, value: string | string[]) => void;
  multiple?: boolean;
  isNested?: boolean;
  clearValue?: string;
}) => {
  const { classes } = formInputsStyles();
  const formattedOptions = formatOptions(options);
  label = label || identifier.charAt(0).toUpperCase() + identifier.slice(1);
  const [selected, setSelected] = useState<string | string[]>(defaultValue);

  // The filter bars derive `defaultValue` from the URL, so "reset filters" and
  // browser navigation change it after mount; without this the Select would keep
  // showing whatever was picked before the reset. Compared by value, not by
  // reference: a `multiple` default is an array the parent rebuilds every render,
  // and resyncing on that would clobber the user's choice on every keystroke.
  const defaultKey = JSON.stringify(defaultValue ?? null);
  const [lastDefaultKey, setLastDefaultKey] = useState(defaultKey);
  if (defaultKey !== lastDefaultKey) {
    setLastDefaultKey(defaultKey);
    setSelected(defaultValue);
  }

  const isCleared =
    clearValue !== undefined &&
    (selected === clearValue ||
      selected === "" ||
      (Array.isArray(selected) && selected.length === 0));

  // `clearValue` is a real value ("all"), not an empty one, so MUI's FormControl
  // sees a filled input and shrinks the label — floating it above an empty-looking
  // box, unlike every other input on the same filter row. Shrink is therefore
  // decided here rather than left to MUI, and follows focus the way a TextField's
  // own label does.
  const [isFocused, setIsFocused] = useState(false);
  const labelSitsInside = isCleared && !isFocused;

  // An outlined Select with `displayEmpty` is forced to `notched: true`
  // (MUI `Select.js`), so the outline keeps a gap for a label that is meant to be
  // sitting inside it. It is only needed when the value really is empty — a
  // `clearValue` such as "all" is a value, and `renderValue` blanks it anyway.
  const valueIsEmpty =
    selected === "" || (Array.isArray(selected) && selected.length === 0);

  // `clearValue` ("all") is deliberately absent from `options`, and MUI warns on a
  // value matching no `MenuItem`. The sentinel stays in state — the parent's
  // `onChange` contract expects it back — and only the Select sees an empty value.
  const selectValue =
    clearValue !== undefined && selected === clearValue
      ? multiple
        ? []
        : ""
      : selected;

  const handleClear = (e: React.MouseEvent) => {
    e.stopPropagation();
    const value = clearValue ?? "";
    setSelected(value);
    onChange(identifier, value);
  };

  return (
    <FormControl
      className={`${classes.select} ${
        isNested ? classes.connectivityInputReversed : ""
      }`}
      fullWidth
    >
      <InputLabel id={`${identifier}-select-label`} shrink={!labelSitsInside}>
        {label}
      </InputLabel>
      <Select
        id={`${identifier}-select-service`}
        labelId={`${identifier}-select-label`}
        value={selectValue}
        // Omitted while the label sits inside, so the outline has no notch to
        // cut around a label that is not above it.
        label={labelSitsInside ? undefined : label}
        required={required}
        onFocus={() => setIsFocused(true)}
        onBlur={() => setIsFocused(false)}
        onChange={(event) => {
          const value = event.target.value as string | string[];
          setSelected(value);
          onChange(identifier, value);
        }}
        multiple={multiple}
        displayEmpty={valueIsEmpty}
        renderValue={(val) => {
          if (clearValue !== undefined && (val === clearValue || val === ""))
            return "";
          const display = Array.isArray(val)
            ? val.join(", ")
            : formattedOptions[val as string] ?? String(val);
          return display.startsWith("<") ? <em>{display}</em> : display;
        }}
        sx={{ textTransform: "capitalize" }}
        endAdornment={
          !isCleared ? (
            <InputAdornment position="end" sx={{ mr: 2 }}>
              <IconButton size="small" onClick={handleClear} sx={{ p: 0.25 }}>
                <ClearIcon fontSize="small" />
              </IconButton>
            </InputAdornment>
          ) : undefined
        }
      >
        {Object.entries(formattedOptions).map(([key, value], index) => (
          <MenuItem
            key={index}
            value={key}
            sx={{ textTransform: "capitalize" }}
          >
            {multiple && (
              <Checkbox
                checked={
                  Array.isArray(selected)
                    ? selected.includes(key)
                    : selected === key
                }
              />
            )}
            <ListItemText
              className={classes.inputText}
              primary={value.startsWith("<") ? <em>{value}</em> : value}
              sx={{ margin: 0 }}
            />
          </MenuItem>
        ))}
      </Select>
      {description && <FormHelperText>{description}</FormHelperText>}
    </FormControl>
  );
};
